import React from 'react';
import { compose, join, map, replace, filter, split, always, any } from 'ramda';

import { APPLICATION_ROLES, ALLERGENS, ALLERGEN_EXCEPTIONS, ALLERGEN_EXACT_EXCEPTIONS, isAllergenException } from 'components/constants';
import InputTextField from 'components/inputfields/InputTextField';
import { useDebouncedStateString } from 'hooks/useDebouncedState';

import { keyIngredients } from '../constants';
import { useProductValidations } from '../validation-context/ProductValidationContext';
import AiFieldVerdict from '../AiFieldVerdict';

export const checkIngredientStatementError = ingredientStatement => {
  let e = false;
  let ing = undefined;
  const value = ingredientStatement;
  if (value && value.trim !== '') {
    const regex = new RegExp(`([a-zA-ZäöåÄÖÅ0-9]*(?:${keyIngredients.join('|')})[a-zA-ZäöåÄÖÅ0-9]*)`, 'gmi');
    // const regex = new RegExp(`([a-zA-ZäöåÄÖÅ0-9]*${keyIngredients.join('|')}[a-zA-ZäöåÄÖÅ0-9]*)`, 'gmi');
    ing = regex.exec(value)?.[1];
    if (ing) {
      const regex1 = /(%|100\s*g)/;
      if (!regex1.test(value)) {
        e = true;
      }
    }
  }

  return { error: e, ingredient: ing };
};

export const getAllergens = ingredientStatement => {
  const processText = compose(
    list => ({ length: list.length, value: join(', ', list) }),
    map(word => `"${word}"`),
    map(replace(/"|'|,|\./g, '')),
    filter(word => word !== word.toUpperCase()),
    filter(word => {
      // First check if it's an exception
      if (isAllergenException(word)) {
        return false;
      }

      // Clean the word for checking against allergens
      const cleanWord = word.toLowerCase().replace(/[*]/g, '');
      const cleanExactWord = cleanWord.replace(/^[\s"'()[\]{}]+|[\s"'()[\]{}]+$/g, '');

      if (ALLERGEN_EXACT_EXCEPTIONS.includes(cleanExactWord)) {
        return false;
      }

      // Then check if it contains an allergen
      return any(allergen => {
        // Simple case - word contains allergen
        if (cleanWord.includes(allergen)) {
          // For words with slashes, check each part separately
          if (cleanWord.includes('/')) {
            const parts = cleanWord
              .split(/[\/]/)
              .map(part => part.replace(/^[\s"'()[\]{}]+|[\s"'()[\]{}]+$/g, ''));
            // Only consider it an allergen if a complete part matches
            // or if it's not part of an exception
            return parts.some(
              part =>
                part === allergen ||
                (part.includes(allergen) &&
                  !ALLERGEN_EXACT_EXCEPTIONS.includes(part) &&
                  !ALLERGEN_EXCEPTIONS.some(exc => part.startsWith(exc.split('/')[0])))
            );
          }
          // For regular words, it's an allergen if not part of an exception
          return !ALLERGEN_EXCEPTIONS.some(exc => cleanWord.startsWith(exc));
        }
        return false;
      }, ALLERGENS);
    }),
    split(/[\s,\.!?:;()]+/),
    always(ingredientStatement)
  );

  return processText();
};

export const Ingredients = ({ state, setState, userRole, approve, rootCategory, readOnly = false }) => {
  const { requiredFields } = useProductValidations();
  const isSuperuser = userRole === APPLICATION_ROLES.SUPERUSER;

  const [ingredientsError, setIngredientsError] = React.useState(false);
  const [ingredient, setIngredient] = React.useState(undefined);

  // Use debounced state hook for immediate UI feedback with debounced parent updates
  const [localValue, handleDebouncedChange] = useDebouncedStateString(
    state.ingredient_statement || '',
    setState,
    'ingredient_statement',
    300
  );

  React.useEffect(() => {
    const { error, ingredient } = checkIngredientStatementError(state.ingredient_statement);
    setIngredientsError(error);
    setIngredient(ingredient);
  }, [state, state.ingredient_statement]);

  const handleChange = event => {
    const stateValue = event.target.value;
    console.log(`NAME: ${event.target.name} - VALUE: ${stateValue}`);
    handleDebouncedChange(stateValue);
  };
  const allergens = React.useMemo(() => {
    return getAllergens(state.ingredient_statement);
  }, [state.ingredient_statement]); // Fixed dependency array

  return (
    <>
      <InputTextField
        readOnly={readOnly}
        style={{ marginTop: 7 }}
        label="Ingrediensförteckning"
        multiline
        spellCheck
        setState={setState}
        approve={isSuperuser && approve}
        state={state}
        inputName="ingredient_statement"
        hideAiVerdict
        onChange={handleChange}
        multilineRows={2}
        multilineMax={20}
        value={localValue}
        withLabel
        forceRed={allergens.length > 0 || ingredientsError}
        inputClassName="data-hj-allow"
        hideComponentLabel={readOnly}
        labelOptions={{
          required: !!requiredFields.ingredient_statement,
          heading: 'Ingrediensförteckning',
          text: 'Ingrediensförteckning som är angiven på förpackning. Markera allergener med VERSALER. Huvudregeln är att alla ingredienser som har använts för att tillverka livsmedlet ska finnas med i ingrediensförteckningen. De ska stå i fallande storleksordning efter den vikt de hade vid tillverkningstillfället.',
          link: 'https://kontrollwiki.livsmedelsverket.se/artikel/43/ingredienser',
        }}
      />
      {allergens.length > 0 && (
        <span style={{ fontWeight: 'bold', color: 'orangered' }}>
          Det verkar som att {allergens.value} är {allergens.length === 1 ? 'en allergen' : 'allergener'}. Om detta
          stämmer, var god skriv {allergens.length === 1 ? 'denna' : 'dessa'} i VERSALER
        </span>
      )}
      {ingredientsError && (
        <>
          {allergens.length > 0 && <span>. </span>}
          <span style={{ fontWeight: 'bold', color: 'orangered' }}>
            Det verkar som att "{ingredient}" behöver anges med en mängdangivelse. Om detta stämmer, var god ange mängd
            för "{ingredient}"
          </span>
        </>
      )}
      {/* The AI review verdict comes after the field's own checks. */}
      <AiFieldVerdict field="ingredient_statement" />
    </>
  );
};

export default Ingredients;
